import { useState, useCallback, useRef, useEffect } from "react";
import { cn } from "../lib/utils";
import type { PhaseOneAnalysis, PhaseOneItemStatus } from "../lib/phaseOneAnalysis";
import {
  Download,
  Search,
  ChevronDown,
  ChevronRight,
  AlertTriangle,
  Pill,
  CheckCircle2,
  XCircle,
  Loader2,
  FileText,
  ExternalLink,
  FileSearch,
  Globe2,
  Info,
} from "lucide-react";

export interface SummaryIdentityItem {
  label: string;
  value?: string;
  hint?: string;
  accent?: boolean;
  format?: "formula" | "text";
}

export interface SummaryApprovalRow {
  region: string;
  agency: string;
  product?: string;
  ingredient?: string;
  strength?: string;
  holder?: string;
  status?: string;
  date?: string;
  href?: string;
  linkLabel?: string;
}

export interface SummaryEvidenceLink {
  source: string;
  type: string;
  title: string;
  href?: string;
  date?: string;
  note?: string;
  priority?: "high" | "medium" | "standard";
}

export interface SummaryPreviewCardProps {
  drugName: { cn: string; en: string; inn: string };
  dosageForm: string;
  pkParams: {
    t12: string;
    bioavailability: string;
    cmax?: string;
    auc?: string;
    tmax?: string;
    vd?: string;
    cl?: string;
    proteinBinding?: string;
    notes?: string;
  };
  sourceCoverage: { covered: number; total: number };
  qualityScore: number;
  isCombination?: boolean;
  components?: Array<{ cn: string; en: string }>;
  identityItems?: SummaryIdentityItem[];
  approvalRows?: SummaryApprovalRow[];
  evidenceLinks?: SummaryEvidenceLink[];
  phaseOneAnalysis?: PhaseOneAnalysis;
  onDownload?: () => Promise<void>;
  onViewDetail?: () => void;
}

const PHASE_ONE_STATUS: Record<PhaseOneItemStatus, { label: string; className: string }> = {
  ready: { label: "已具备", className: "bg-success/10 text-success" },
  partial: { label: "需补充", className: "bg-warning/10 text-warning" },
  missing: { label: "待获取", className: "bg-destructive/10 text-destructive" },
  review: { label: "需核对", className: "bg-warning/10 text-warning" },
};

function PhaseOneAnalysisPanel({ analysis }: { analysis: PhaseOneAnalysis }) {
  const overallLabel = analysis.overallStatus === "complete"
    ? "资料较完整"
    : analysis.overallStatus === "partial"
      ? "部分具备"
      : "证据不足";
  const overallClass = analysis.overallStatus === "complete"
    ? "text-success"
    : analysis.overallStatus === "partial"
      ? "text-warning"
      : "text-destructive";

  return (
    <section className="border-y border-border py-4" aria-labelledby="phase-one-analysis-title">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <FileSearch className="h-4 w-4 text-primary" />
            <h3 id="phase-one-analysis-title" className="text-sm font-semibold text-foreground">
              第一阶段：药物基础与研究资料分析
            </h3>
          </div>
          <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
            {analysis.summary}
          </p>
        </div>
        <div className="flex flex-shrink-0 items-baseline gap-2 rounded-md border border-border px-3 py-2">
          <span className={cn("text-lg font-bold tabular-nums", overallClass)}>
            {analysis.readinessScore}
          </span>
          <span className="text-xs text-muted-foreground">/100 资料准备度</span>
          <span className={cn("text-xs font-semibold", overallClass)}>{overallLabel}</span>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto rounded-md border border-border">
        <table aria-label="第一阶段药物分析核查表" className="min-w-[48rem] w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
              <th className="w-44 px-3 py-2.5 text-left font-medium">核查项</th>
              <th className="w-24 px-3 py-2.5 text-left font-medium">状态</th>
              <th className="px-3 py-2.5 text-left font-medium">专业结论</th>
              <th className="w-72 px-3 py-2.5 text-left font-medium">证据摘要</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/70">
            {analysis.items.map((analysisItem) => {
              const status = PHASE_ONE_STATUS[analysisItem.status];
              return (
                <tr key={analysisItem.id} className="align-top hover:bg-muted/20">
                  <td className="px-3 py-2.5 font-medium text-foreground">{analysisItem.label}</td>
                  <td className="px-3 py-2.5">
                    <span className={cn("inline-flex rounded-md px-2 py-1 text-xs font-semibold", status.className)}>
                      {status.label}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 leading-relaxed text-foreground">{analysisItem.conclusion}</td>
                  <td className="px-3 py-2.5 break-words leading-relaxed text-muted-foreground">
                    {analysisItem.evidence || "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {analysis.nextActions.length > 0 && (
        <div className="mt-4 grid gap-2 sm:grid-cols-[9rem_1fr]">
          <div className="text-xs font-semibold uppercase text-muted-foreground">进入方案设计前</div>
          <ol className="space-y-1.5 text-sm leading-relaxed text-foreground">
            {analysis.nextActions.map((action, index) => (
              <li key={action} className="flex gap-2">
                <span className="font-semibold tabular-nums text-primary">{index + 1}.</span>
                <span>{action}</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="mt-4 space-y-1 border-t border-border/70 pt-3 text-xs leading-relaxed text-muted-foreground">
        {analysis.limitations.map((limitation) => (
          <p key={limitation}>{limitation}</p>
        ))}
      </div>
    </section>
  );
}

type DownloadStatus = "idle" | "loading" | "success" | "error";

function QualityScoreBadge({ score }: { score: number }) {
  const colorClass =
    score >= 85
      ? "bg-success/15 text-success border-success/30"
      : score >= 70
        ? "bg-warning/15 text-warning border-warning/30"
        : "bg-destructive/15 text-destructive border-destructive/30";

  const label =
    score >= 85 ? "优秀" : score >= 70 ? "良好" : "需改进";

  return (
    <div className={cn("inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm font-semibold", colorClass)}>
      <span>{score}/100</span>
      <span className="text-xs font-normal opacity-70">{label}</span>
    </div>
  );
}

function SourceProgressBar({ covered, total }: { covered: number; total: number }) {
  const safeTotal = Math.max(total, 1);
  const percentage = total > 0 ? Math.round((covered / safeTotal) * 100) : 0;
  const filledBlocks = Math.min(8, total > 0 ? Math.round((covered / safeTotal) * 8) : 0);
  const emptyBlocks = Math.max(0, 8 - filledBlocks);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">数据源覆盖</span>
        <span className="font-medium text-foreground">{covered}/{total}</span>
      </div>
      <div className="flex items-center gap-1">
        {Array.from({ length: filledBlocks }).map((_, i) => (
          <div key={`filled-${i}`} className="w-3 h-3 rounded-sm bg-primary" />
        ))}
        {Array.from({ length: emptyBlocks }).map((_, i) => (
          <div key={`empty-${i}`} className="w-3 h-3 rounded-sm bg-muted" />
        ))}
        <span className="ml-2 text-xs text-muted-foreground">{percentage}%</span>
      </div>
    </div>
  );
}

function hasDisplayValue(value?: string) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  return Boolean(text && text !== "—" && text !== "-");
}

function MolecularFormulaText({ value }: { value: string }) {
  const chunks: Array<{ text: string; subscript: boolean }> = [];
  let buffer = "";
  let isDigitBuffer = false;

  for (const char of value) {
    const isDigit = /\d/.test(char);
    if (buffer && isDigit !== isDigitBuffer) {
      chunks.push({ text: buffer, subscript: isDigitBuffer });
      buffer = "";
    }
    buffer += char;
    isDigitBuffer = isDigit;
  }
  if (buffer) chunks.push({ text: buffer, subscript: isDigitBuffer });

  return (
    <span aria-label={value}>
      {chunks.map((chunk, index) =>
        chunk.subscript ? (
          <sub key={`${chunk.text}-${index}`} className="align-sub text-[0.72em] leading-none">
            {chunk.text}
          </sub>
        ) : (
          <span key={`${chunk.text}-${index}`}>{chunk.text}</span>
        ),
      )}
    </span>
  );
}

function IdentityOverview({ items }: { items: SummaryIdentityItem[] }) {
  const visibleItems = items.filter((item) => hasDisplayValue(item.value)).slice(0, 8);
  if (visibleItems.length === 0) return null;

  return (
    <section className="rounded-lg border border-border bg-muted/10 p-4">
      <div className="mb-3 flex items-center gap-2">
        <Info className="h-4 w-4 text-primary" />
        <h3 className="text-sm font-semibold text-foreground">药品身份概览</h3>
      </div>
      <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {visibleItems.map((item) => (
          <div key={`${item.label}-${item.value}`} className="min-w-0">
            <dt className="text-xs text-muted-foreground">{item.label}</dt>
            <dd
              className={cn(
                "mt-1 break-words text-sm leading-relaxed",
                item.accent ? "font-semibold text-primary" : "font-medium text-foreground",
              )}
            >
              {item.format === "formula" && item.value ? (
                <MolecularFormulaText value={item.value} />
              ) : (
                item.value
              )}
            </dd>
            {item.hint && (
              <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">
                {item.hint}
              </p>
            )}
          </div>
        ))}
      </dl>
    </section>
  );
}

function ApprovalOverviewTable({ rows }: { rows: SummaryApprovalRow[] }) {
  const visibleRows = rows
    .filter((row) => hasDisplayValue(row.product) || hasDisplayValue(row.ingredient))
    .slice(0, 6);
  if (visibleRows.length === 0) return null;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Globe2 className="h-4 w-4 text-primary" />
          全球批准 / 注册概况
        </h3>
        <span className="text-xs text-muted-foreground">
          {visibleRows.length} 个来源
        </span>
      </div>
      <div className="overflow-x-auto rounded-md border border-border">
        <table aria-label="全球批准注册概况表" className="min-w-[54rem] w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
              <th className="px-3 py-2.5 text-left font-medium">地区 / 机构</th>
              <th className="px-3 py-2.5 text-left font-medium">药品名称</th>
              <th className="px-3 py-2.5 text-left font-medium">活性成分</th>
              <th className="px-3 py-2.5 text-left font-medium">规格</th>
              <th className="px-3 py-2.5 text-left font-medium">持有人 / 企业</th>
              <th className="px-3 py-2.5 text-left font-medium">状态</th>
              <th className="px-3 py-2.5 text-left font-medium">资料</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/70">
            {visibleRows.map((row, index) => (
              <tr key={`${row.agency}-${row.product || row.ingredient}-${index}`} className="hover:bg-muted/20">
                <td className="px-3 py-2.5 align-top">
                  <div className="font-semibold text-foreground">{row.region}</div>
                  <div className="text-xs text-muted-foreground">{row.agency}</div>
                </td>
                <td className="px-3 py-2.5 align-top font-medium text-foreground">
                  {row.product || "—"}
                  {row.date && (
                    <div className="mt-1 text-xs font-normal text-muted-foreground">
                      {row.date}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5 align-top text-muted-foreground">{row.ingredient || "—"}</td>
                <td className="px-3 py-2.5 align-top text-muted-foreground">{row.strength || "—"}</td>
                <td className="px-3 py-2.5 align-top text-muted-foreground">{row.holder || "—"}</td>
                <td className="px-3 py-2.5 align-top">
                  {row.status ? (
                    <span className="inline-flex rounded-md bg-success/10 px-2 py-1 text-xs font-medium text-success">
                      {row.status}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-3 py-2.5 align-top">
                  {row.href ? (
                    <a
                      href={row.href}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-primary hover:underline"
                    >
                      {row.linkLabel || "打开"}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function EvidenceLinkCenter({ links }: { links: SummaryEvidenceLink[] }) {
  const visibleLinks = links
    .filter((link) => hasDisplayValue(link.title))
    .slice(0, 10);
  if (visibleLinks.length === 0) return null;

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <FileSearch className="h-4 w-4 text-primary" />
          官方证据链接
        </h3>
        <span className="text-xs text-muted-foreground">
          优先显示审评、说明书、指导原则和关键文献
        </span>
      </div>
      <div className="grid gap-2 sm:grid-cols-2">
        {visibleLinks.map((link, index) => {
          const isHigh = link.priority === "high";
          const content = (
            <>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={cn(
                      "rounded px-1.5 py-0.5 text-[11px] font-semibold",
                      isHigh ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
                    )}>
                      {link.source}
                    </span>
                    <span className="text-xs text-muted-foreground">{link.type}</span>
                    {link.date && (
                      <span className="text-xs text-muted-foreground">{link.date}</span>
                    )}
                  </div>
                  <div className="mt-1 break-words text-sm font-medium leading-relaxed text-foreground">
                    {link.title}
                  </div>
                  {link.note && (
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      {link.note}
                    </p>
                  )}
                </div>
                {link.href && <ExternalLink className="mt-1 h-3.5 w-3.5 flex-shrink-0 text-primary" />}
              </div>
            </>
          );

          return link.href ? (
            <a
              key={`${link.source}-${link.title}-${index}`}
              href={link.href}
              target="_blank"
              rel="noreferrer"
              className={cn(
                "block rounded-md border border-border bg-card p-3 transition-colors hover:border-primary/50 hover:bg-primary/5",
                isHigh && "border-primary/30",
              )}
            >
              {content}
            </a>
          ) : (
            <div
              key={`${link.source}-${link.title}-${index}`}
              className="rounded-md border border-border bg-muted/10 p-3"
            >
              {content}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function CombinationComponents({
  components,
}: {
  components: Array<{ cn: string; en: string }>;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-md border border-warning/30 bg-warning/5 overflow-hidden transition-all duration-200">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-warning/10 transition-colors"
      >
        <AlertTriangle className="w-4 h-4 text-warning flex-shrink-0" />
        <span className="font-medium text-warning">
          复方制剂 ({components.length}种活性成分)
        </span>
        {expanded ? (
          <ChevronDown className={cn("w-4 h-4 text-warning ml-auto transition-transform duration-200")} />
        ) : (
          <ChevronRight className={cn("w-4 h-4 text-warning ml-auto transition-transform duration-200")} />
        )}
      </button>
      <div
        className={cn(
          "overflow-hidden transition-all duration-300 ease-in-out",
          expanded ? "max-h-40 opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <div className="px-3 pb-2 space-y-1">
          {components.map((comp, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2 text-sm py-1 pl-6 animate-slideDown"
              style={{ animationDelay: `${idx * 50}ms` }}
            >
              <span className="text-muted-foreground text-xs select-none">
                {idx === components.length - 1 ? "└─" : "├─"}
              </span>
              <span className="text-muted-foreground">组分{idx + 1}:</span>
              <span className="font-medium text-foreground">{comp.cn}</span>
              <span className="text-muted-foreground">({comp.en})</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function DownloadButton({
  status,
  onClick,
}: {
  status: DownloadStatus;
  onClick: () => void;
}) {
  if (status === "loading") {
    return (
      <button disabled className="btn btn-primary opacity-80 cursor-wait gap-2">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span>生成中...</span>
        <span className="inline-block w-16 h-1.5 bg-white/20 rounded-full overflow-hidden">
          <span className="block h-full w-2/3 bg-white/60 animate-pulse rounded-full" style={{ animationDuration: "1s" }} />
        </span>
      </button>
    );
  }
  if (status === "success") {
    return (
      <button className="btn bg-success text-white hover:bg-success/90 border-0 gap-2">
        <CheckCircle2 className="w-4 h-4" />
        下载完成
      </button>
    );
  }
  if (status === "error") {
    return (
      <button onClick={onClick} className="btn btn-danger gap-2">
        <XCircle className="w-4 h-4" />
        重试下载
      </button>
    );
  }
  return (
    <button onClick={onClick} className="btn btn-primary gap-2">
      <Download className="w-4 h-4" />
      下载完整报告
    </button>
  );
}

interface PKParamItem {
  key: string;
  notation: React.ReactNode;
  label: string;
  value: string | undefined;
  fallbackUnit?: string;
}

function normalizePkTableValue(value: string | undefined, fallbackUnit?: string) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text || text === "—" || text === "-") return "";
  if (text.length > 160) return "";
  if (
    /(pharmacokinetics|clinical pharmacology|after a single|following oral|section\s*\d+|^\d+\.\d+\s+[A-Z][a-z]+)/i.test(text) &&
    text.length > 35
  ) {
    return "";
  }
  if (!fallbackUnit || /[%a-zA-Zμµ]/.test(text)) return text;
  return `${text} ${fallbackUnit}`;
}

function PkNotation({ type }: { type: string }) {
  switch (type) {
    case "cmax":
      return <><i>C</i><sub>max</sub></>;
    case "auc":
      return <i>AUC</i>;
    case "tmax":
      return <><i>T</i><sub>max</sub></>;
    case "t12":
      return <><i>t</i><sub>1/2</sub></>;
    case "bioavailability":
      return <i>F</i>;
    case "vd":
      return <><i>V</i><sub>d</sub></>;
    case "cl":
      return <i>CL</i>;
    case "proteinBinding":
      return <i>PPB</i>;
    default:
      return <i>{type}</i>;
  }
}

function PKParamsGrid({ pkParams }: { pkParams: SummaryPreviewCardProps["pkParams"] }) {
  const [showNotes, setShowNotes] = useState(false);

  const params: PKParamItem[] = [
    {
      key: "cmax",
      notation: <PkNotation type="cmax" />,
      label: "峰浓度",
      value: pkParams.cmax,
    },
    {
      key: "auc",
      notation: <PkNotation type="auc" />,
      label: "药时曲线下面积",
      value: pkParams.auc,
    },
    {
      key: "tmax",
      notation: <PkNotation type="tmax" />,
      label: "达峰时间",
      value: pkParams.tmax,
      fallbackUnit: "h",
    },
    {
      key: "t12",
      notation: <PkNotation type="t12" />,
      label: "消除半衰期",
      value: pkParams.t12,
      fallbackUnit: "h",
    },
    {
      key: "bioavailability",
      notation: <PkNotation type="bioavailability" />,
      label: "生物利用度",
      value: pkParams.bioavailability,
      fallbackUnit: "%",
    },
    {
      key: "vd",
      notation: <PkNotation type="vd" />,
      label: "表观分布容积",
      value: pkParams.vd,
    },
    {
      key: "cl",
      notation: <PkNotation type="cl" />,
      label: "清除率",
      value: pkParams.cl,
    },
    {
      key: "proteinBinding",
      notation: <PkNotation type="proteinBinding" />,
      label: "血浆蛋白结合率",
      value: pkParams.proteinBinding,
      fallbackUnit: "%",
    },
  ];

  const visibleParams = params
    .map((param) => ({
      ...param,
      value: normalizePkTableValue(param.value, param.fallbackUnit),
    }))
    .filter((param) => param.value);
  const hasNotes = pkParams.notes && pkParams.notes.trim().length > 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Pill className="w-4 h-4 text-primary" />
          药代动力学参数 (PK)
        </h4>
        <span className="text-xs text-muted-foreground">
          {visibleParams.length} 项参数
        </span>
      </div>

      {visibleParams.length > 0 ? (
        <div className="overflow-x-auto rounded-md border border-border">
          <table aria-label="药代动力学参数表" className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
                <th className="w-28 px-3 py-2.5 text-left font-medium">参数</th>
                <th className="px-3 py-2.5 text-left font-medium">统计含义</th>
                <th className="min-w-[12rem] px-3 py-2.5 text-left font-medium">数值 / 单位</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/70">
              {visibleParams.map((param) => (
                <tr key={param.key} className="hover:bg-muted/20">
                  <td className="px-3 py-2.5 text-base font-semibold text-foreground">
                    {param.notation}
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground">
                    {param.label}
                  </td>
                  <td className="px-3 py-2.5 font-medium tabular-nums text-foreground">
                    {param.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="rounded-md border border-dashed border-border bg-muted/10 px-3 py-3 text-sm text-muted-foreground">
          暂无可表格化的关键 PK 参数；完整原文可在下方 FDA 数据源中查看。
        </div>
      )}

      {hasNotes && (
        <div className="rounded-lg border border-border bg-muted/10 p-3">
          <button
            onClick={() => setShowNotes(!showNotes)}
            className="flex items-center gap-2 w-full text-left"
          >
            <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              来源说明
            </span>
            {showNotes ? (
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground ml-auto" />
            ) : (
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground ml-auto" />
            )}
          </button>
          {showNotes && (
            <p className="mt-2 text-sm text-foreground leading-relaxed pl-6">
              {pkParams.notes}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function SummaryPreviewCard({
  drugName,
  dosageForm,
  pkParams,
  sourceCoverage,
  qualityScore,
  isCombination = false,
  components = [],
  identityItems = [],
  approvalRows = [],
  evidenceLinks = [],
  phaseOneAnalysis,
  onDownload,
  onViewDetail,
}: SummaryPreviewCardProps) {
  const [downloadStatus, setDownloadStatus] = useState<DownloadStatus>("idle");
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const current = pollRef.current;
    return () => {
      if (current) clearInterval(current);
    };
  }, []);

  const handleDownload = useCallback(async () => {
    if (!onDownload || downloadStatus === "loading") return;
    setDownloadStatus("loading");
    try {
      await onDownload();
      setDownloadStatus("success");
    } catch {
      setDownloadStatus("error");
    }
  }, [onDownload, downloadStatus]);

  return (
    <div className="card animate-fadeIn overflow-hidden">
      <div className="p-5 sm:p-6 space-y-5">
        {/* Header: Drug Name + Icon */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <div className="flex min-w-0 flex-1 items-start gap-4">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Pill className="w-6 h-6 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-bold text-foreground leading-tight break-words">
                {drugName.cn}
              </h2>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                <span className="text-sm text-muted-foreground break-all">
                  {drugName.inn || drugName.en}
                </span>
                <span className="badge badge-info">{dosageForm}</span>
              </div>
            </div>
          </div>
          <div className="self-start flex-shrink-0">
            <QualityScoreBadge score={qualityScore} />
          </div>
        </div>

        {/* Combination Drug Warning */}
        {isCombination && components.length > 0 && (
          <CombinationComponents components={components} />
        )}

        {/* Identity Snapshot */}
        <IdentityOverview items={identityItems} />

        {/* Approval / Registration Snapshot */}
        <ApprovalOverviewTable rows={approvalRows} />

        {/* Phase-one evidence-based analysis */}
        {phaseOneAnalysis && <PhaseOneAnalysisPanel analysis={phaseOneAnalysis} />}

        {/* PK Parameters - Structured Display */}
        <PKParamsGrid pkParams={pkParams} />

        {/* Official Evidence Links */}
        <EvidenceLinkCenter links={evidenceLinks} />

        {/* Source Coverage Progress */}
        <SourceProgressBar
          covered={sourceCoverage.covered}
          total={sourceCoverage.total}
        />

        {/* Divider */}
        <div className="divider" />

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <DownloadButton status={downloadStatus} onClick={handleDownload} />
          {onViewDetail && (
            <button onClick={onViewDetail} className="btn btn-secondary gap-2">
              <Search className="w-4 h-4" />
              在线查看详情
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
